<template>
  <div
    id="dashboard"
    class="pa-4"
  >
    <v-row
      v-if="goalkeepers.length"
    >
      <v-col class="text-h6">
        {{ goalkeepersLabel }}
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-for="player in goalkeepers"
        :key="player.id"
        cols="auto"
      >
        <player-card
          :player="player"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="defenders.length"
    >
      <v-col class="text-h6">
        {{ defendersLabel }}
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-for="player in defenders"
        :key="player.id"
        cols="auto"
      >
        <player-card
          :player="player"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="midfielders.length"
    >
      <v-col class="text-h6">
        {{ midfieldersLabel }}
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-for="player in midfielders"
        :key="player.id"
        cols="auto"
      >
        <player-card
          :player="player"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="forwards.length"
    >
      <v-col class="text-h6">
        {{ forwardsLabel }}
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-for="player in forwards"
        :key="player.id"
        cols="auto"
      >
        <player-card
          :player="player"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="nonDefinedPositionsPlayers.length && !user.sport.is_individual"
    >
      <v-col class="text-h6">
        {{ $t('main_dashboard.players_list.no_position.label') }}
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-for="player in nonDefinedPositionsPlayers"
        :key="player.id"
        cols="auto"
      >
        <player-card
          :player="player"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="coaches.data && coaches.data.length"
    >
      <v-col class="text-h6">
        {{ $t('main_dashboard.players_list.staff.label') }}
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-for="coach in coaches.data"
        :key="coach.id"
        cols="auto"
      >
        <player-card
          :player="coach"
          :is-staff="true"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>

import { CoachApiService, PlayerApiService } from '@/data/services'
import PlayerCard from '@/components/players/PlayerCard.vue'
import { orderBy } from 'lodash'

export default {
  name: 'DashboardPlayersView',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    PlayerCard
  },
  data: () => ({
    players: {
      data: null,
      loading: false
    },
    coaches: {
      data: null,
      loading: false
    }
  }),
  computed: {
    goalkeepers() {
      return orderBy(this.players.data?.filter(player => player.player_positions[0]?.line === 1) || [], 'jersey_number')
    },
    defenders() {
      return orderBy(this.players.data?.filter(player => player.player_positions[0]?.line === 2) || [], 'jersey_number')
    },
    midfielders() {
      return orderBy(this.players.data?.filter(player => player.player_positions[0]?.line === 3) || [], 'jersey_number')
    },
    forwards() {
      return orderBy(this.players.data?.filter(player => player.player_positions[0]?.line === 4) || [], 'jersey_number')
    },
    goalkeepersLabel() {
      return this.$t('main_dashboard.players_list.goalkeepers.label')
    },
    defendersLabel() {
      if (this.user.sport.name === 'Handball') {
        return this.$t('main_dashboard.players_list.backs.label')
      } else if (this.user.sport.name === 'Basketball') {
        return this.$t('main_dashboard.players_list.guards.label')
      } else {
        return this.$t('main_dashboard.players_list.defenders.label')
      }
    },
    midfieldersLabel() {
      if (this.user.sport.name === 'Football') {
        return this.$t('main_dashboard.players_list.midfielders.label')
      } else if (this.user.sport.name === 'Handball') {
        return this.$t('main_dashboard.players_list.wingers.label')
      } else if (this.user.sport.name === 'Basketball') {
        return this.$t('main_dashboard.players_list.forwards.label')
      } else if (this.user.sport.name === 'Hockey') {
        return this.$t('main_dashboard.players_list.wings.label')
      }
      return ''
    },
    forwardsLabel() {
      if (this.user.sport.name === 'Football') {
        return this.$t('main_dashboard.players_list.forwards.label')
      } else if (this.user.sport.name === 'Handball') {
        return this.$t('main_dashboard.players_list.pivots.label')
      } else if (this.user.sport.name === 'Basketball') {
        return this.$t('main_dashboard.players_list.centers.label')
      } else if (this.user.sport.name === 'Hockey') {
        return this.$t('main_dashboard.players_list.centers.label')
      }
      return ''
    },
    nonDefinedPositionsPlayers() {
      return this.players.data?.filter(player => player.player_positions.length === 0) || []
    },
    selectedTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    }
  },
  watch: {
    selectedTeam: function () {
      this.fetchAllPlayers()
      this.fetchAllCoaches()
    }
  },
  created() {
    this.fetchAllPlayers()
    this.fetchAllCoaches()
  },
  methods: {
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC', team_id: this.selectedTeam.id })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    async fetchAllCoaches() {
      try {
        this.coaches.loading = true

        const result = await CoachApiService.getPaginated({
          order_by: 'last_name',
          order: 'ASC',
          team_id: this.selectedTeam.id
        })

        this.coaches.data = result.data
      } finally {
        this.coaches.loading = false
      }
    }
  }
}
</script>
